window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  // B4 (2026-08-02) — dual-read for customer-facing keys renamed to hidden
  // underscore form ('_Key' new, 'Key' pre-rename carts). See cart-validator.
  function _dualProp(props, key) {
    if (!props) return undefined;
    var u = props['_' + key];
    return (typeof u !== 'undefined') ? u : props[key];
  }


  var EventBus = JRS.EventBus;
  var CartAPI = JRS.CartAPI;

  var _boundListeners = [];
  var _eventUnsubs = [];
  var _boundDocEvents = [];
  var _cartData = null;
  var _itemMap = null;
  var _rootEl = null;
  var _qtyInputTimers = {};
  // Per-row qty-change lock. The qty-stepper chain interleaves /cart/change.js
  // calls when users click ± rapidly, racing on stale keys and producing drift
  // on bundle children. Tracking by data-jrs-item-key lets us drop concurrent
  // clicks on the same row until the in-flight chain completes.
  var _qtyChangeInFlight = {};
  var _debugEnabled = true;

  function _debugLog() {
    if (!_debugEnabled || typeof console === 'undefined' || typeof console.log !== 'function') return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(_getDebugPrefix('JRS CartPage'));
    console.log.apply(console, args);
  }

  function _debugWarn() {
    if (!_debugEnabled || typeof console === 'undefined' || typeof console.warn !== 'function') return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(_getDebugPrefix('JRS CartPage'));
    console.warn.apply(console, args);
  }

  function _getDebugPrefix(scope) {
    var themeId = String(window._bcThemeId || window._mrkThemeId || 'base');
    var themeVersion = String(window._bcThemeVersion || window._mrkThemeVersion || 'JRSBASE-V8.3.0-09102026-BYOB');
    return '[base v' + themeVersion.replace(/^\d+-/, '') + '][betterconversions][' + themeId + '][' + themeVersion + '][' + String(scope || 'log') + ']';
  }

  /**
   * Initialize the cart page module.
   * Called by CartController after Liquid renders the page.
   *
   * @param {Object} [cartData]
   * @returns {void}
   */
  function init(cartData) {
    destroy();

    _rootEl = document.querySelector('[data-jrs-cart-page]') || document;
    _debugLog('init', {
      pathname: window.location && window.location.pathname,
      hasRootHook: !!document.querySelector('[data-jrs-cart-page]'),
      usingDocumentFallback: _rootEl === document
    });

    _cartData = cartData || _readCartJSON();
    _itemMap = _buildParentChildMap();
    _debugLog('cart bootstrap data', {
      hasCartData: !!_cartData,
      itemCount: _cartData && typeof _cartData.item_count === 'number' ? _cartData.item_count : null
    });

    _bindQuantitySteppers();
    _bindRemoveButtons();
    _bindDiscountApplyButtons();
    _bindEditButtons();
    _bindCheckoutButton();

    if (EventBus && typeof EventBus.on === 'function') {
      _eventUnsubs.push(EventBus.on('cart:updated', function(data) {
        // Phase 4 cart-edit reload trigger.
        //
        // cart-edit-flow.js emits `cart:updated` with `reason: 'cart_edit'`
        // after a successful atomic remove-then-add save (see
        // dev/assets/jrs-v2-cart-edit-flow.js around line 364). The cart
        // page's line-item DOM is owned by the initial Liquid render and
        // doesn't have a section-rendering or in-place re-render path
        // client-side — only `_updateCartTotals()` refreshes from
        // /cart.js. For qty change / remove / add the original line key
        // stays in place, so the existing DOM row remains valid and a
        // totals refresh is sufficient. Cart-edit, by contrast, mints
        // brand-new line keys + a fresh `_bundle_instance_id` on every
        // save, so the existing DOM rows are stale by construction.
        //
        // Pattern matches the existing error-recovery reloads at
        // cart-page.js:399 (qty change failure), :687 (remove failure),
        // and :1692 (other recovery), AND the original pre-Phase-4 dead
        // code cart-edit-flow.js which reloaded after every successful
        // edit. Phase 5 polish: implement a `/cart?section_id=...` based
        // partial re-render so the cart page stays mounted across edits,
        // documented in `docs/phase-2-status-and-resume-notes.md` Phase 5
        // polish list.
        if (data && data.reason === 'cart_edit') {
          if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
            window.location.reload();
          }
          return;
        }
        _updateCartTotals();
      }));

      // STOPGAP(per-product-edit): surface the popup shell's edit-blocked
      // notice. openFromCartLine refuses to open per-product bundles (whose
      // cart-edit save would corrupt the line) and emits cart:editBlocked with
      // the resolved copy; show it in the existing cart notification region.
      // Remove with the gate when the _bundle_raw hydration shim lands.
      _eventUnsubs.push(EventBus.on('cart:editBlocked', function(data) {
        _showNotification((data && data.message) || '', 'info');
      }));
      // END STOPGAP(per-product-edit)
    }
    _bindLegacyCartEvents();

    _ensureEmptyState();
    // Bundle summary rendering is owned by the standalone JRS.BundleSummary
    // module (jrs-v2-bundle-summary.js), which self-mounts on DOM-ready and
    // re-renders on the `cart:updated` EventBus signal. The cart page no longer
    // renders bundle composition itself.
  }

  /**
   * Tear down all event listeners and clear state.
   * @returns {void}
   */
  function destroy() {
    _destroyListeners();
    _destroyEventSubscriptions();
    _destroyDocEventSubscriptions();
    _clearQtyInputTimers();
    _cartData = null;
    _itemMap = null;
    _rootEl = null;
  }

  /**
   * @returns {Object|null}
   */
  function _readCartJSON() {
    if (!_rootEl) return null;
    var jsonEl = _rootEl.querySelector('[data-jrs-cart-json]');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent || '{}');
    } catch (error) {
      console.error('[CartPage] Failed to parse [data-jrs-cart-json]', error);
      return null;
    }
  }

  /**
   * @returns {{ parents: Element[], children: Element[], plain: Element[], bundles: Element[] }}
   */
  function _classifyItems() {
    var result = { parents: [], children: [], plain: [], bundles: [] };
    if (!_rootEl) return result;

    var items = _rootEl.querySelectorAll('[data-jrs-cart-item]');
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      var type = el.getAttribute('data-jrs-item-type');
      if (type === 'plain') {
        result.plain.push(el);
      } else if (type === 'customized-parent') {
        result.parents.push(el);
      } else if (type === 'bundle-parent') {
        result.bundles.push(el);
      }
    }

    return result;
  }

  /**
   * Build map: parentKey -> child keys list.
   *
   * @returns {{ byParentKey: Object<string, string[]>, classified: { parents: Element[], children: Element[], plain: Element[], bundles: Element[] } }}
   */
  function _buildParentChildMap() {
    var byParentKey = {};
    var classified = _classifyItems();
    var parentsAndBundles = classified.parents.concat(classified.bundles);

    for (var i = 0; i < parentsAndBundles.length; i++) {
      var row = parentsAndBundles[i];
      var parentKey = row.getAttribute('data-jrs-item-key') || '';
      if (!parentKey) continue;
      byParentKey[parentKey] = _parseChildKeys(row.getAttribute('data-jrs-child-keys') || '');
    }

    return {
      byParentKey: byParentKey,
      classified: classified
    };
  }

  /**
   * @param {string} childKeysStr
   * @returns {string[]}
   */
  function _parseChildKeys(childKeysStr) {
    var raw = String(childKeysStr || '').split(',');
    var keys = [];
    for (var i = 0; i < raw.length; i++) {
      var key = String(raw[i] || '').trim();
      if (key.length > 0) keys.push(key);
    }
    return keys;
  }

  function _bindQuantitySteppers() {
    if (!_rootEl) return;
    var container = _rootEl.querySelector('[data-jrs-cart-items]');
    if (!container) {
      _debugWarn('quantity container missing: [data-jrs-cart-items]');
      return;
    }

    _bindListener(container, 'click', function(e) {
      var minusBtn = e.target && e.target.closest ? e.target.closest('[data-jrs-qty-minus]') : null;
      var plusBtn = e.target && e.target.closest ? e.target.closest('[data-jrs-qty-plus]') : null;
      if (!minusBtn && !plusBtn) return;
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();

      var row = e.target.closest('[data-jrs-cart-item]');
      if (!row) return;
      var input = row.querySelector('[data-jrs-qty-input]');
      if (!input) return;

      // Per-row lock — drop the click if a qty change is already in flight for this row.
      // Without this, rapid ± clicks interleave their /cart/change.js chains, producing
      // bundle child qty drift (paid positions stick at intermediate values).
      var rowItemKey = row.getAttribute('data-jrs-item-key') || '';
      if (rowItemKey && _qtyChangeInFlight[rowItemKey]) {
        _debugLog('qty click dropped: change already in flight', { itemKey: rowItemKey });
        return;
      }

      var moq = _readMOQ(input);
      var currentQty = _normalizeCurrentQty(input, Number(input.value || 0));
      var newQty = currentQty;
      if (moq > 0) {
        if (plusBtn) {
          newQty = currentQty === 0 ? moq : currentQty + moq;
        } else {
          newQty = currentQty <= moq ? 0 : currentQty - moq;
        }
      } else {
        if (minusBtn) newQty = currentQty - 1;
        if (plusBtn) newQty = currentQty + 1;
        if (newQty < 0) newQty = 0;
      }

      _clearQtyTimerForRow(row);

      input.value = String(newQty);
      _onQuantityChange(row, newQty, currentQty);
    });

    var commitManualQtyChange = function(target, immediate) {
      if (!target || !target.matches || !target.matches('[data-jrs-qty-input]')) return;
      var row = target.closest('[data-jrs-cart-item]');
      if (!row) return;

      var rowKey = row.getAttribute('data-jrs-item-key') || '';
      if (!rowKey) return;

      var currentQty = Number(target.defaultValue || target.value || 0);
      var parsedQty = Number(target.value || 0);
      var normalizedQty = _enforceMOQ(target, parsedQty);
      target.value = String(normalizedQty);

      _clearQtyTimerForKey(rowKey);
      if (immediate) {
        if (normalizedQty === currentQty) {
          return;
        }
        // Per-row lock — drop manual commit if a chain is already in flight.
        if (_qtyChangeInFlight[rowKey]) {
          _debugLog('manual qty commit dropped: change already in flight', { itemKey: rowKey });
          return;
        }
        _onQuantityChange(row, normalizedQty, currentQty);
        return;
      }

      _qtyInputTimers[rowKey] = setTimeout(function() {
        if (normalizedQty !== currentQty && !_qtyChangeInFlight[rowKey]) {
          _onQuantityChange(row, normalizedQty, currentQty);
        }
        _clearQtyTimerForKey(rowKey);
      }, 700);
    };

    _bindListener(container, 'input', function(e) {
      commitManualQtyChange(e.target, false);
    });

    _bindListener(container, 'focusout', function(e) {
      commitManualQtyChange(e.target, true);
    });
  }

  /**
   * @param {HTMLInputElement|Element} inputEl
   * @param {number} value
   * @returns {number}
   */
  function _enforceMOQ(inputEl, value) {
    var nextQty = Number(value || 0);
    if (nextQty < 0) nextQty = 0;
    var moq = _readMOQ(inputEl);
    if (moq > 0 && nextQty > 0) {
      if (nextQty < moq) {
        return moq;
      }
      // MOQ products use multiples only.
      return Math.floor(nextQty / moq) * moq;
    }
    return nextQty;
  }

  /**
   * @param {Element} rowEl
   * @param {number} newQty
   * @param {number} prevQty
   * @returns {void}
   */
  function _onQuantityChange(rowEl, newQty, prevQty) {
    if (!rowEl) return;

    var itemKey = rowEl.getAttribute('data-jrs-item-key') || '';
    if (!itemKey) return;
    _debugLog('quantity change requested', {
      itemKey: itemKey,
      prevQty: prevQty,
      newQty: newQty
    });
    // Acquire per-row lock. Released in onSuccess/onError below. Combined with
    // the click-handler guard, this serialises qty changes for a single row.
    _qtyChangeInFlight[itemKey] = true;
    _setRowLoading(rowEl, true);

    var childKeys = _getChildKeysForParent(itemKey, rowEl);
    var childQtyRatios = _getChildQtyRatiosForParent(itemKey, rowEl, childKeys);
    var itemType = rowEl.getAttribute('data-jrs-item-type') || '';
    var isBundleParent = itemType === 'bundle-parent';
    var isCustomizedParent = itemType === 'customized-parent';
    var needsSequentialSync = isBundleParent || isCustomizedParent;

    var childTargets = [];
    for (var i = 0; i < childKeys.length; i++) {
      var childKey = childKeys[i];
      var ratio = Number(childQtyRatios[childKey] || 1);
      if (!isFinite(ratio) || ratio <= 0) ratio = 1;
      var childTargetQty = Math.round(newQty * ratio);
      if (newQty > 0 && childTargetQty <= 0) {
        _debugWarn('zero-qty guard triggered', {
          childKey: childKey,
          ratio: ratio,
          parentQty: newQty
        });
        childTargetQty = Math.max(1, Math.round(ratio));
      }
      childTargets.push({ key: childKey, qty: Math.max(0, childTargetQty) });
    }

    var unitPrice = _readRowUnitPrice(rowEl);
    _updateLinePrice(rowEl, unitPrice, newQty);
    if (newQty === 0) {
      rowEl.style.display = 'none';
    }

    function onSuccess(result) {
      _debugLog('qty update resolved', {
        ok: !!(result && result.ok),
        error: result && result.error ? result.error : null,
        itemKey: itemKey,
        path: needsSequentialSync ? 'sequential_change' : 'batch_update'
      });
      if (result && result.ok) {
        _cartData = result.cart || _cartData;
        var input = rowEl.querySelector('[data-jrs-qty-input]');
        if (input) input.defaultValue = String(newQty);

        // Shopify rewrites a line item's `key` whenever its quantity changes, so
        // the rowEl's `data-jrs-item-key` (rendered at page load) is stale after
        // the first successful change. Subsequent clicks would then call
        // /cart/change.js with a non-existent key and the change quietly fails.
        // Resolve the row's NEW key from the fresh cart by re-using a stable
        // identifier:
        //   • bundle parents → match by `_bundle_instance_id` (unique per add)
        //   • customized parents → match by `_lineItemKey` then `_ParentKey`
        // and rewrite the DOM attribute + _itemMap so the next click is keyed
        // correctly. Without this, multi-bundle scenarios (two bundles same
        // variant_id) end up reverted to qty=1 because every click after the
        // first hits a stale key.
        if (rowEl && _cartData && Array.isArray(_cartData.items)) {
          var newKey = '';
          if (isBundleParent && parentInstanceId) {
            for (var nki = 0; nki < _cartData.items.length; nki++) {
              var nkItem = _cartData.items[nki];
              if (!nkItem || !nkItem.properties) continue;
              if (nkItem.properties._ParentVariant) continue; // skip children
              if (Number(nkItem.variant_id || 0) !== Number(parentVariantId || 0)) continue;
              if (String(nkItem.properties._bundle_instance_id || '') === String(parentInstanceId)) {
                newKey = String(nkItem.key || '');
                break;
              }
            }
          }
          if (newKey && newKey !== itemKey) {
            rowEl.setAttribute('data-jrs-item-key', newKey);
            if (_itemMap && _itemMap.byParentKey) {
              if (_itemMap.byParentKey[itemKey]) {
                _itemMap.byParentKey[newKey] = _itemMap.byParentKey[itemKey];
                delete _itemMap.byParentKey[itemKey];
              }
            }
            _debugLog('parent key refreshed after qty change', { oldKey: itemKey, newKey: newKey });
            itemKey = newKey;
          }
        }

        if (newQty === 0) {
          _removeDiscountWarningForRow(rowEl);
          if (rowEl.parentNode) {
            rowEl.parentNode.removeChild(rowEl);
          }
          if (_itemMap && _itemMap.byParentKey) {
            delete _itemMap.byParentKey[itemKey];
          }
        }
        if (_isCartNowEmpty(result.cart)) {
          window.location.reload();
          return;
        }
        _updateCartTotals();
        _refreshCartSection(); // re-render the per-item discount messaging (Liquid-computed) for the new qty
        EventBus.emit('cart:quantityChanged', {
          lineKey: itemKey,
          oldQuantity: prevQty,
          newQuantity: newQty,
          variantId: _readRowVariantId(rowEl)
        });
        _ensureEmptyState(result.cart);
        _setRowLoading(rowEl, false);

        // Release lock + re-run cart validation. validateCart's bundle drift
        // detector heals any qty drift the chain produced (paid-position children
        // stuck at intermediate values) before the user notices or checks out.
        delete _qtyChangeInFlight[itemKey];
        // Pass the changed row + a STABLE match context (instance id + variant)
        // so the post-heal repaint can re-sync this row's qty input + price from
        // server truth. parentInstanceId / parentVariantId are set by the
        // sequential-sync branch above for bundle/customized parents; non-bundle
        // lines never heal, so the fallbacks here are inert.
        _runDriftValidation(rowEl, {
          isBundleParent: isBundleParent,
          instanceId: (parentInstanceId || ''),
          variantId: (parentVariantId || Number(rowEl.getAttribute('data-jrs-item-variant-id') || 0))
        });
        return;
      }
      delete _qtyChangeInFlight[itemKey];

      _revertQuantityUI(rowEl, prevQty, unitPrice);
      _setRowLoading(rowEl, false);
    }

    function onError(error) {
      _debugWarn('qty update rejected', {
        itemKey: itemKey,
        message: error && error.message ? error.message : 'unknown',
        path: needsSequentialSync ? 'sequential_change' : 'batch_update'
      });
      // Instrumentation (2026-09-03): this failure reverts the stepper with no message.
      try {
        if ((window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function')) {
          JRS.ErrorTelemetry.report('cart', 'qty_change_failed', {
            silent: true,
            detail: String((error && error.message) || 'unknown'),
            http_status: (error && typeof error.status === 'number') ? error.status : null,
            line_key: String(itemKey || ''),
            old_quantity: prevQty,
            new_quantity: newQty,
            path: needsSequentialSync ? 'sequential_change' : 'batch_update'
          });
        }
      } catch (e2) {}
      _revertQuantityUI(rowEl, prevQty, unitPrice);
      _setRowLoading(rowEl, false);
      delete _qtyChangeInFlight[itemKey];
    }

    if (needsSequentialSync) {
      // Customized-parent qty sync (bundle-parent + customized-parent), Shopify-
      // key safety notes:
      //
      // 1. /cart/update.js batch `updates` hash is NOT safe. Shopify parses the
      //    numeric prefix of any line-item key it receives as a variant_id, so
      //    two children sharing a variant collapse into one entry and the loser
      //    gets driven to 0 on a decrease — permanent deletion. Must use
      //    sequential /cart/change.js keyed by id.
      //
      // 2. Shopify regenerates line-item keys on quantity changes. Both the
      //    changed item's own key AND sibling keys on the same variant_id are
      //    rewritten. Keys captured from the page-load DOM (data-jrs-child-keys)
      //    are stale after the first round, and keys captured at the start of a
      //    round are stale after the first child's call. Re-resolving by the
      //    stable `_lineItemKey` custom property against the freshest cart is
      //    required before each /cart/change.js call.
      //
      // 3. The Liquid template emits an empty data-jrs-child-keys whenever two
      //    cart rows share the parent's variant_id (e.g. plain + customized of
      //    the same product), so we cannot rely on the DOM child list. Source
      //    children from live _cartData by matching _ParentVariant.
      //
      // 4. Individual child failures must NOT short-circuit the chain — the
      //    remaining children still need their qty aligned with the new parent
      //    qty. Log and continue.

      // Resolve parent from live cart. _cartData is kept fresh by onSuccess
      // after each prior qty change, so keys and properties are current.
      var parentItem = null;
      if (_cartData && Array.isArray(_cartData.items)) {
        for (var pi = 0; pi < _cartData.items.length; pi++) {
          if (_cartData.items[pi] && String(_cartData.items[pi].key || '') === String(itemKey)) {
            parentItem = _cartData.items[pi];
            break;
          }
        }
      }
      var parentVariantId = parentItem ? Number(parentItem.variant_id || 0) : 0;
      var parentInstanceId = isBundleParent && parentItem && parentItem.properties
        ? String(parentItem.properties._bundle_instance_id || '')
        : '';
      // Standard duplicate-add row scope (2026-08-02) — see _customization_instance_id.
      var parentCustomInstanceId = (!isBundleParent && parentItem && parentItem.properties)
        ? String(parentItem.properties._customization_instance_id || '')
        : '';

      var syncChildTargets = [];
      if (_cartData && Array.isArray(_cartData.items) && parentVariantId) {
        for (var bi = 0; bi < _cartData.items.length; bi++) {
          var cItem = _cartData.items[bi];
          if (!cItem || !cItem.properties) continue;
          if (Number(cItem.properties._ParentVariant || 0) !== parentVariantId) continue;

          if (isBundleParent) {
            // Scope to this specific bundle row when _bundle_instance_id is
            // present on both sides, so multiple bundle instances of the same
            // variant don't cross over. Fall through to variant-only match if
            // either side is missing the instance id.
            if (parentInstanceId && cItem.properties._bundle_instance_id) {
              if (String(cItem.properties._bundle_instance_id) !== parentInstanceId) continue;
            }
          } else {
            // Customized-parent: the row-scope id is authoritative when both
            // sides carry it (2026-08-02); _ParentKey next; else variant-only.
            if (parentCustomInstanceId && cItem.properties._customization_instance_id &&
                String(cItem.properties._customization_instance_id) !== parentCustomInstanceId) continue;
            var cpk = cItem.properties._ParentKey ? String(cItem.properties._ParentKey) : '';
            if (cpk && cpk !== String(itemKey)) continue;
          }

          // Ratio: bundle children use _qty as per-parent-unit multiplier;
          // customized children are 1:1 with the parent qty.
          var ratio = 1;
          if (isBundleParent) {
            var baseQty = parseInt(cItem.properties._qty, 10);
            if (isFinite(baseQty) && baseQty > 0) ratio = baseQty;
          }
          var tq = Math.round(newQty * ratio);
          if (newQty > 0 && tq <= 0) {
            _debugWarn('zero-qty guard triggered (' + (isBundleParent ? 'bundle' : 'customized') + ')', {
              lineItemKey: cItem.properties._lineItemKey,
              ratio: ratio,
              parentQty: newQty
            });
            tq = Math.max(1, ratio);
          }
          syncChildTargets.push({
            lineItemKey: String(cItem.properties._lineItemKey || ''),
            // Garment variant — the ONLY field distinguishing per-size lines that
            // share one _lineItemKey (and one cart variant_id = positionVariantId).
            // Without it the apply step's first-match resolve collapses the sizes.
            productVariantId: String(cItem.properties._ProductVariantId || ''),
            initialKey: String(cItem.key || ''),
            targetQty: Math.max(0, tq)
          });
        }
      }

      // Fallback: if live-cart resolution yielded nothing (first click before
      // _cartData exists, or malformed state), use the DOM-sourced childTargets
      // so we still attempt the legacy path rather than silently skipping.
      if (syncChildTargets.length === 0) {
        syncChildTargets = childTargets.map(function(t) {
          return { lineItemKey: '', initialKey: t.key, targetQty: t.qty };
        });
      }

      var lastOkResult = null;
      var failedChildren = [];
      var latestCart = _cartData;
      var syncLabel = isBundleParent ? 'bundle' : 'customized';

      var chain = CartAPI.changeItem(itemKey, { quantity: newQty }).then(function(parentResult) {
        if (parentResult && parentResult.ok) {
          lastOkResult = parentResult;
          if (parentResult.cart) latestCart = parentResult.cart;
        }
        return parentResult;
      });

      syncChildTargets.forEach(function(target) {
        chain = chain.then(function() {
          // Resolve THIS specific size line by the composite (_lineItemKey +
          // _ProductVariantId). Resolving by _lineItemKey alone returns the first
          // match, collapsing every size of a position onto one line so only one
          // scales. Both fields survive Shopify's per-change.js rekeys (only the
          // cart `key` hash changes); the captured initialKey is the defensive
          // fallback when the composite doesn't resolve.
          var freshItem = target.lineItemKey
            ? _findChildLine(latestCart, target.lineItemKey, target.productVariantId)
            : null;
          var keyToUse = freshItem ? String(freshItem.key || '') : (target.initialKey || '');

          if (!keyToUse) {
            failedChildren.push({
              lineItemKey: target.lineItemKey,
              attemptedKey: null,
              targetQty: target.targetQty,
              error: 'no_resolved_key'
            });
            _debugWarn(syncLabel + ' child qty change skipped (no resolved key)', {
              lineItemKey: target.lineItemKey,
              targetQty: target.targetQty
            });
            return { ok: false, cart: null, error: 'no_resolved_key' };
          }

          return CartAPI.changeItem(keyToUse, { quantity: target.targetQty }).then(function(childResult) {
            if (childResult && childResult.ok) {
              lastOkResult = childResult;
              if (childResult.cart) latestCart = childResult.cart;
            } else {
              failedChildren.push({
                lineItemKey: target.lineItemKey,
                attemptedKey: keyToUse,
                targetQty: target.targetQty,
                error: childResult && childResult.error
              });
              _debugWarn(syncLabel + ' child qty change failed (after key refresh)', {
                lineItemKey: target.lineItemKey,
                attemptedKey: keyToUse,
                targetQty: target.targetQty,
                error: childResult && childResult.error
              });
            }
            return childResult;
          });
        });
      });

      chain.then(function() {
        if (failedChildren.length > 0) {
          _debugWarn(syncLabel + ' qty sync completed with failures', {
            parentKey: itemKey,
            failedCount: failedChildren.length,
            failed: failedChildren
          });
        }
        var finalResult = lastOkResult || { ok: false, cart: latestCart, error: 'no_successful_updates' };
        onSuccess(finalResult);
      }).catch(onError);
    } else {
      var updates = {};
      updates[itemKey] = newQty;
      for (var j = 0; j < childTargets.length; j++) {
        updates[childTargets[j].key] = childTargets[j].qty;
      }
      CartAPI.updateItems(updates).then(onSuccess).catch(onError);
    }
  }

  function _bindRemoveButtons() {
    if (!_rootEl) return;
    var container = _rootEl.querySelector('[data-jrs-cart-items]');
    if (!container) return;

    _bindListener(container, 'click', function(e) {
      var removeBtn = e.target && e.target.closest ? e.target.closest('[data-jrs-remove-item]') : null;
      if (!removeBtn) return;

      var row = removeBtn.closest('[data-jrs-cart-item]');
      if (!row) return;

      var itemKey = row.getAttribute('data-jrs-item-key') || '';
      if (!itemKey) return;
      var childKeys = _getChildKeysForParent(itemKey, row);
      _removeItem(row, itemKey, childKeys);
    });
  }

  function _bindDiscountApplyButtons() {
    if (!_rootEl) return;
    var container = _rootEl.querySelector('[data-jrs-cart-items]');
    if (!container) return;

    _bindListener(container, 'click', function(e) {
      var applyBtn = e.target && e.target.closest ? e.target.closest('.cart-item-discount-apply-button') : null;
      if (!applyBtn) return;
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();

      var row = applyBtn.closest('[data-jrs-cart-item]');
      if (!row) return;
      var input = row.querySelector('[data-jrs-qty-input]');
      if (!input) return;

      var currentQty = Number(input.value || input.defaultValue || 0);
      var threshold = Number(
        applyBtn.getAttribute('data-threshold') ||
        row.getAttribute('data-tier1-threshold') ||
        10
      );
      if (!isFinite(threshold) || threshold <= 0) threshold = 10;
      var nextQty = _enforceMOQ(input, threshold);

      if (nextQty === currentQty) return;
      input.value = String(nextQty);
      _onQuantityChange(row, nextQty, currentQty);
    });
  }

  /**
   * @param {Element} rowEl
   * @param {string} itemKey
   * @param {string[]} childKeys
   * @returns {void}
   */
  function _removeItem(rowEl, itemKey, childKeys) {
    _setRowLoading(rowEl, true);
    // CLS round 2 (2026-10-15): collapse the row at the tap, not after the API reply. A collapse
    // seconds after the tap counts as a layout shift (lab 0.14 on mobile; field culprit
    // jrs-main-cart__summary, top on the cart after the 10 Oct fixes); no score for a shift
    // within 500 ms of a tap. Restore the row on every failure path below.
    _setRowCollapsed(rowEl, true);
    _clearQtyTimerForKey(itemKey);

    var itemType = rowEl ? (rowEl.getAttribute('data-jrs-item-type') || '') : '';
    var isBundleParent = itemType === 'bundle-parent';
    var isCustomizedParent = itemType === 'customized-parent';

    function applyRemovalResult(result) {
      if (result && result.ok) {
        _cartData = result.cart || _cartData;
        _removeDiscountWarningForRow(rowEl);
        if (rowEl.parentNode) {
          rowEl.parentNode.removeChild(rowEl);
        }
        if (_itemMap && _itemMap.byParentKey) {
          delete _itemMap.byParentKey[itemKey];
        }
        if (_isCartNowEmpty(result.cart)) {
          window.location.reload();
          return;
        }
        _updateCartTotals();
        _refreshCartSection(); // remaining items' discount tiers may shift after a removal
        _cleanupOrphanDiscountWarnings();
        EventBus.emit('cart:itemRemoved', {
          lineKey: itemKey,
          variantId: _readRowVariantId(rowEl),
          wasCustomized: rowEl.getAttribute('data-jrs-item-type') !== 'plain'
        });
        _ensureEmptyState(result.cart);
        _toggleFullCartLoader();
        return;
      }
      _setRowCollapsed(rowEl, false);
      _setRowLoading(rowEl, false);
    }

    // Plain line (no customization children) — single removal, no scoping needed.
    if (!isBundleParent && !isCustomizedParent) {
      CartAPI.removeItems([itemKey]).then(function (result) {
        // Instrumentation (2026-09-03): a non-ok removal only cleared the spinner.
        if (!result || !result.ok) {
          try {
            if ((window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function')) {
              JRS.ErrorTelemetry.report('cart', 'line_removal_failed', {
                silent: true, line_key: String(itemKey || ''), detail: String((result && result.error) || 'not_ok')
              });
            }
          } catch (e2) {}
        }
        return applyRemovalResult(result);
      }).catch(function(err) {
        try {
          if ((window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function')) {
            JRS.ErrorTelemetry.report('cart', 'line_removal_failed', {
              silent: true, line_key: String(itemKey || ''), detail: String((err && err.message) || 'threw')
            });
          }
        } catch (e2) {}
        _setRowCollapsed(rowEl, false);
        _setRowLoading(rowEl, false);
      });
      return;
    }

    // D-1 step 2 (Bug F / ROOT-1): resolve children LIVE by their stable `_lineItemKey`
    // (scoped by _bundle_instance_id / _ParentKey) and remove them SEQUENTIALLY via
    // removeItemsByLineItemKey — which re-resolves each key per /cart/change.js response
    // — instead of the batch /cart/update.js `updates` hash, which collapses same-variant
    // child siblings and silently leaves a paid orphan (the canonical Bug F deletion).
    // The page-load `data-jrs-child-keys` list is no longer a mutation source (kept only
    // as a Bug-W fallback below). Children first (resolved while the parent is present),
    // then the parent line by its re-resolved key.
    function legacyBatchRemove() {
      // Last-resort fallback when the live cart is unreadable (Bug W): never regress to
      // "remove nothing" — fall back to the previous batch path with the DOM child keys.
      var keysToRemove = [itemKey];
      for (var i = 0; i < (childKeys || []).length; i++) keysToRemove.push(childKeys[i]);
      return CartAPI.removeItems(keysToRemove);
    }

    CartAPI.getCart().then(function(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return legacyBatchRemove();
      }
      var parentItem = null;
      for (var pi = 0; pi < cart.items.length; pi++) {
        if (cart.items[pi] && String(cart.items[pi].key || '') === String(itemKey)) { parentItem = cart.items[pi]; break; }
      }
      var parentVariantId = parentItem ? Number(parentItem.variant_id || 0) : 0;
      var parentInstanceId = (isBundleParent && parentItem && parentItem.properties)
        ? String(parentItem.properties._bundle_instance_id || '') : '';

      var childLineItemKeys = _resolveChildLineItemKeysLive(cart, itemKey, isBundleParent, isCustomizedParent);

      // v8.1.1 (orphaned instances — orders #72575, #73582): when the parent carries
      // an instance id, remove EVERY line of that instance by its own Shopify key
      // (parent included) in one batch. The _lineItemKey path stays only as the
      // legacy fallback for lines minted before instance ids existed — it removes
      // one line per key and can hit the other instance's children.
      var parentCustomInstanceId = (isCustomizedParent && parentItem && parentItem.properties)
        ? String(parentItem.properties._customization_instance_id || '') : '';
      var hasInstance = !!(parentInstanceId || parentCustomInstanceId) && typeof CartAPI.removeItemsByInstance === 'function';

      if (hasInstance) {
        return CartAPI.removeItemsByInstance({
          bundleInstanceId: parentInstanceId,
          customizationInstanceId: parentCustomInstanceId,
          // HotFix 2026-09-25: a standard add shares one instance id across every
          // size row; remove only this row's size (see CartAPI.removeItemsByInstance).
          parentVariantId: (parentCustomInstanceId && !parentInstanceId && parentVariantId) ? parentVariantId : '',
          includeParent: true,
          cart: cart
        }).then(function(instRes) {
          if (!instRes || !instRes.ok) {
            try {
              if (window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function') {
                JRS.ErrorTelemetry.report('cart', 'instance_removal_incomplete', {
                  silent: true, detail: String((instRes && instRes.error) || 'unknown'),
                  remaining: (instRes && instRes.remaining) || -1, line_key: String(itemKey || '')
                });
              }
            } catch (e2) {}
          }
          return { ok: !!(instRes && instRes.ok), cart: (instRes && instRes.cart) || cart };
        });
      }

      var removeChildren = childLineItemKeys.length
        ? CartAPI.removeItemsByLineItemKey(childLineItemKeys)
        : Promise.resolve({ ok: true, cart: cart, removed: 0, notFound: 0, failed: 0 });

      return removeChildren.then(function(childRes) {
        var freshCart = (childRes && childRes.cart) || cart;
        var parentKeyNow = _resolveParentKeyLive(freshCart, itemKey, parentVariantId, parentInstanceId, isBundleParent);
        return CartAPI.changeItem(parentKeyNow, { quantity: 0 }).then(function(parentRes) {
          return {
            ok: !!(parentRes && parentRes.ok) && !!(childRes && childRes.ok),
            cart: (parentRes && parentRes.cart) || freshCart,
            error: (childRes && !childRes.ok) ? 'child_remove_failed' : ((parentRes && parentRes.error) || null)
          };
        });
      });
    }).then(applyRemovalResult).catch(function() {
      _setRowCollapsed(rowEl, false);
      _setRowLoading(rowEl, false);
    });
  }

  function _bindEditButtons() {
    if (!_rootEl) return;
    var container = _rootEl.querySelector('[data-jrs-cart-items]');
    if (!container) return;

    _bindListener(container, 'click', function(e) {
      var editBtn = e.target && e.target.closest ? e.target.closest('[data-jrs-edit-customization]') : null;
      if (!editBtn) return;
      _onEditClick(editBtn);
    });
  }

  /**
   * @param {Element} editBtn
   * @returns {void}
   */
  function _onEditClick(editBtn) {
    var parentKey = editBtn.getAttribute('data-parent-key') || '';
    var childKeys = _parseChildKeys(editBtn.getAttribute('data-child-keys') || '');
    var productUrl = editBtn.getAttribute('data-product-url') || '';
    var productType = editBtn.getAttribute('data-product-type') === 'bundle' ? 'bundle' : 'standard';

    EventBus.emit('cart:editRequested', {
      parentKey: parentKey,
      childKeys: childKeys,
      productUrl: productUrl,
      productType: productType
    });
  }

  function _bindCheckoutButton() {
    if (!_rootEl) return;
    var checkoutBtns = _rootEl.querySelectorAll('[data-jrs-checkout-btn], [data-jrs-sticky-checkout-btn]');
    for (var i = 0; i < checkoutBtns.length; i++) {
      _bindListener(checkoutBtns[i], 'click', _onCheckoutClick);
    }
  }

  /**
   * @param {MouseEvent|Event} e
   * @returns {void}
   */
  function _onCheckoutClick(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    var button = e && e.currentTarget ? e.currentTarget : null;
    // Already in flight (disabled after the first click) — ignore, so a double
    // click never re-emits tracking or re-runs validation.
    if (button && button.hasAttribute('disabled')) return;

    // Tracking: jrs_cart_checkout_click — once per click, synchronous (before
    // any navigation) from the live cart snapshot _cartData keeps fresh.
    EventBus.emit('cart:checkoutClicked', {
      itemCount: _cartData && typeof _cartData.item_count === 'number' ? _cartData.item_count : 0,
      totalPrice: _cartData && typeof _cartData.total_price === 'number' ? _cartData.total_price : 0
    });

    if (button) {
      button.setAttribute('disabled', '');
      button.classList.add('jrs-btn--loading');
    }

    var validator = JRS.CartValidator;

    if (!validator || typeof validator.validateBeforeCheckout !== 'function') {
      window.location.href = '/checkout';
      return;
    }

    // Pre-checkout validation still runs (EventBus validation:checkoutBlocked / validation:checkoutCleared)
    // but we do not block navigation or show technical issue copy to shoppers.
    //
    // Run validateCart() FIRST so any drift (e.g. bundle child qty drift introduced by rapid
    // qty-stepper clicks on this same cart page) gets auto-healed before the customer hits
    // /checkout with a wrong line price. validateCart is idempotent — if no drift exists, it
    // returns valid and adds ~one /cart.js fetch of latency.
    var runHealThenFlag = function() {
      if (typeof validator.validateCart === 'function') {
        return validator.validateCart().then(function() {
          return validator.validateBeforeCheckout();
        });
      }
      return validator.validateBeforeCheckout();
    };

    runHealThenFlag().then(function(checkoutResult) {
      var blocked = !!(checkoutResult && checkoutResult.canCheckout === false);
      var resultIssues = (checkoutResult && checkoutResult.issues) || [];
      var alreadyRetried = window.location.search.indexOf('validation_retry=1') !== -1;

      // (b) v7.0.0 release gate — a BUNDLE missing-child block is UNHEALABLE until
      // reconstruction (A) lands, so block on the FIRST click with NO retry reload.
      // The validation_retry reload exists to give the page-load validateCart a
      // second pass at *healable* drift; for a bundle-missing it would just reload
      // with no message before an inevitable block — and that reload is the same
      // fail-open path that cleared a cart to the homepage on 2026-06-09. The
      // validator flags the issue with bundleMissing for exactly this branch.
      var hasBundleMissing = false;
      for (var bi = 0; bi < resultIssues.length; bi++) {
        // 2026-10-08: a BYOB kit below its reward tier (jrs-byob-guard.js) cannot heal on reload either.
        if (resultIssues[bi] && (resultIssues[bi].bundleMissing || resultIssues[bi].byob)) { hasBundleMissing = true; break; }
      }
      if (blocked && hasBundleMissing) {
        _showCheckoutBlocked(button, checkoutResult);
        return;
      }

      // First pass (non-bundle critical): may be transient drift the page-load
      // validateCart can heal (e.g. a non-bundle 'missing' reconstructed by
      // _applyFixesSequentially's 'missing' branch). Reload once with the retry
      // flag to give it a second pass.
      if (blocked && !alreadyRetried) {
        var sep = window.location.search ? '&' : '?';
        window.location.href = window.location.pathname + window.location.search + sep + 'validation_retry=1';
        return;
      }

      // (b) Retry exhausted and STILL blocked (a non-bundle issue the heal could
      // not fix). Do NOT fall through to /checkout — that shipped incomplete carts
      // (live repro 2026-06-09: the fail-open bounce ended at /checkout). Block
      // with the actionable message and keep the customer on /cart. The
      // _jrs_validation_summary cart attribute still records the issue for triage.
      if (blocked) {
        _showCheckoutBlocked(button, checkoutResult);
        return;
      }

      window.location.href = '/checkout';
    }).catch(function(error) {
      // (A) FAIL-CLOSED — a thrown/rejected verify (e.g. a WebKit /cart.js read that
      // rejects) is UNVERIFIABLE = not safe = block. NEVER fall through to /checkout
      // on an error. Re-enable the button and keep the shopper on /cart.
      console.error('[CartPage] Checkout validation threw — blocking (fail-closed):', error);
      _showCheckoutBlocked(button, { issues: [{ type: 'unverifiable', details: 'Cart verification failed (' + ((error && error.message) || 'unknown') + ').' }] });
    });
  }

  /**
   * (b) v7.0.0 — block checkout with an actionable, shopper-facing message when
   * validateBeforeCheckout reports an unhealable critical issue after the retry.
   * Re-enables the checkout button and keeps the shopper on /cart (no /checkout).
   * @param {Element|null} button - the checkout button clicked
   * @param {{ issues?: Array }} checkoutResult
   * @returns {void}
   */
  function _showCheckoutBlocked(button, checkoutResult) {
    if (button) {
      button.removeAttribute('disabled');
      button.classList.remove('jrs-btn--loading');
    }
    var firstIssue = checkoutResult && checkoutResult.issues && checkoutResult.issues[0];
    var issueType = (firstIssue && firstIssue.type) || '';
    EventBus.emit('validation:checkoutBlocked', {
      reason: (firstIssue && firstIssue.details) || 'incomplete_bundle',
      blocking: true
    });
    // 'unverifiable' = the cart couldn't be read/verified (empty / sentinel / throw);
    // anything else = a detected fulfilment shortfall (e.g. a missing bundle child).
    // 2026-10-08: the BYOB reward check (jrs-byob-guard.js) carries its own shopper message.
    var message = (issueType === 'byob_reward' && firstIssue.message) ? firstIssue.message
      : (issueType === 'unverifiable')
      ? 'We couldn’t verify your cart, so we’ve paused checkout to be safe. Please refresh the page and try again — if it keeps happening, contact us and we’ll help.'
      : 'A branding item on your bundle is missing, so we can’t start checkout. ' +
        'Please remove and re-add the bundle, or contact us and we’ll sort it for you.';
    _renderCheckoutBlockedMessage(message);
    // Instrumentation (2026-09-03): the shopper-facing block, with the text shown.
    try {
      // 2026-10-08: a BYOB reward block is a shopper choice, not a fault; jrs-byob-guard.js tracks it instead.
      if (issueType !== 'byob_reward' && (window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function')) {
        JRS.ErrorTelemetry.report('checkout', issueType === 'unverifiable' ? 'checkout_blocked_unverifiable' : 'checkout_blocked_bundle_incomplete', {
          message: message,
          detail: String((firstIssue && firstIssue.details) || ''),
          issue_type: String(issueType || ''),
          issue_count: (checkoutResult && checkoutResult.issues && checkoutResult.issues.length) || 0
        });
      }
    } catch (e2) {}
  }

  /**
   * Insert (or update) a shopper-facing alert above the checkout button. Inline
   * styles guarantee visibility without a stylesheet dependency (this is a safety
   * block); the BEM class allows later theming.
   * @param {string} message
   * @returns {void}
   */
  function _renderCheckoutBlockedMessage(message) {
    if (!_rootEl || typeof _rootEl.querySelector !== 'function') return;
    var existing = _rootEl.querySelector('[data-jrs-checkout-blocked]');
    if (!existing) {
      existing = document.createElement('div');
      existing.setAttribute('data-jrs-checkout-blocked', '');
      existing.setAttribute('role', 'alert');
      existing.className = 'jrs-cart__checkout-blocked';
      existing.style.cssText = 'margin:12px 0;padding:12px 14px;border:1px solid #c0392b;' +
        'background:#fdecea;color:#611a15;border-radius:8px;font-size:14px;line-height:1.45;';
      var anchor = _rootEl.querySelector('[data-jrs-checkout-btn], [data-jrs-sticky-checkout-btn]');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(existing, anchor);
      } else {
        var fallbackParent = (_rootEl === document) ? document.body : _rootEl;
        if (fallbackParent && typeof fallbackParent.appendChild === 'function') {
          fallbackParent.appendChild(existing);
        }
      }
    }
    existing.textContent = message;
  }

  /**
   * @param {Element} rowEl
   * @param {number} unitPrice
   * @param {number} qty
   * @returns {void}
   */
  /**
   * True when the row carries a server-rendered volume/bulk-discount badge —
   * meaning item.final_line_price is BELOW unit × qty and the exact figure is
   * server-only. (Not the compare-at "was" price, which is a struck reference,
   * not a line-level discount, and leaves unit × qty as the true total.)
   *
   * @param {Element} rowEl
   * @returns {boolean}
   */
  function _rowHasBulkDiscount(rowEl) {
    return !!(rowEl && rowEl.querySelector('.cart-item-discount-badge-custom, [class*="discount-badge"]'));
  }

  function _updateLinePrice(rowEl, unitPrice, qty, explicitLineTotal) {
    if (!rowEl) return;
    var hasExplicit =
      typeof explicitLineTotal === 'number' && isFinite(explicitLineTotal) && explicitLineTotal >= 0;
    // A server-computed volume/bulk discount makes the real line total LOWER than
    // unit × qty, and that figure is not known client-side. Painting an optimistic
    // unit×qty over a discounted line shows a price HIGHER than the true discounted
    // total — contradicting the order summary and the line's own server-rendered
    // price. When we have no explicit server total, leave a discounted line
    // untouched for the authoritative section re-render (_refreshCartSection) to
    // repaint, and leave data-jrs-line-price unset so _computeTotalsFromRows falls
    // back to data-jrs-item-line-price (the server final_line_price).
    if (!hasExplicit && _rowHasBulkDiscount(rowEl)) return;
    var linePrice = hasExplicit ? explicitLineTotal : Number(unitPrice || 0) * Number(qty || 0);
    rowEl.setAttribute('data-jrs-line-price', String(linePrice));
    var isVatFree = String(rowEl.getAttribute('data-jrs-item-vat-free') || '').toLowerCase() === 'true';
    var linePriceIncVat = isVatFree ? linePrice : Math.round(linePrice * 1.2);
    var compareUnitPrice = Number(
      rowEl.getAttribute('data-jrs-item-compare-at-price') ||
      rowEl.getAttribute('data-compare-at-price') ||
      0
    );
    var hasCompare = compareUnitPrice > Number(unitPrice || 0);
    var compareLinePrice = hasCompare ? compareUnitPrice * Number(qty || 0) : 0;
    var compareLinePriceIncVat = isVatFree ? compareLinePrice : Math.round(compareLinePrice * 1.2);

    var hookedEls = rowEl.querySelectorAll('[data-jrs-item-line-total], [data-jrs-item-line-total-inc], [data-jrs-item-line-total-ex]');
    for (var i = 0; i < hookedEls.length; i++) {
      var hookedEl = hookedEls[i];
      if (!hookedEl) continue;
      var useInc = hookedEl.hasAttribute('data-jrs-item-line-total-inc');
      var useEx = hookedEl.hasAttribute('data-jrs-item-line-total-ex');
      if (!useInc && !useEx) {
        var priceWrapper = hookedEl.closest('.price');
        useInc = !!(priceWrapper && priceWrapper.classList && priceWrapper.classList.contains('in-vat'));
      }
      hookedEl.textContent = _formatMoney(useInc ? linePriceIncVat : linePrice);
    }

    // Legacy fallback: update amount span inside each row price block
    // when explicit hooks are missing (e.g. VAT FREE rows).
    var legacyPriceBlocks = rowEl.querySelectorAll('.pricing [data-price-wrapper] .price');
    for (var j = 0; j < legacyPriceBlocks.length; j++) {
      var block = legacyPriceBlocks[j];
      if (!block) continue;
      var amountEl = _getLegacyRowAmountNode(block);
      if (!amountEl) continue;
      var isIncBlock = block.classList && block.classList.contains('in-vat');
      amountEl.textContent = _formatMoney(isIncBlock ? linePriceIncVat : linePrice);

      var compareEls = block.querySelectorAll('.cart-item-compare-price-custom');
      for (var k = 0; k < compareEls.length; k++) {
        var compareEl = compareEls[k];
        if (!compareEl) continue;
        if (!hasCompare || compareLinePrice <= 0) {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
          continue;
        }
        compareEl.style.display = '';
        compareEl.textContent = _formatMoney(isIncBlock ? compareLinePriceIncVat : compareLinePrice);
      }
    }
  }

  /**
   * For legacy cart row markup, find the amount span to replace:
   * ignore compare-at and VAT label spans.
   *
   * @param {Element} priceBlockEl
   * @returns {Element|null}
   */
  function _getLegacyRowAmountNode(priceBlockEl) {
    if (!priceBlockEl) return null;
    var spans = priceBlockEl.querySelectorAll('span');
    for (var i = 0; i < spans.length; i++) {
      var span = spans[i];
      if (!span) continue;
      if (span.classList && span.classList.contains('cart-item-compare-price-custom')) continue;
      if (span.classList && span.classList.contains('vat-info')) continue;
      if (span.classList && span.classList.contains('text-xs')) continue;
      if (span.classList && span.classList.contains('text-sm')) continue;
      return span;
    }
    return null;
  }

  /**
   * Re-render the per-item discount UI — the "APPLY X% DISCOUNT" button, the discount
   * badge (item.discounts[0].title), and the discounted line price — after a quantity
   * change. These are computed by Liquid from item.discounts / item.quantity and can't
   * be recomputed client-side, so we pull a fresh server render of the main-cart section
   * (Shopify Section Rendering API) and swap the item rows in. The click/input listeners
   * are delegated on the stable [data-jrs-cart-items] container, so they survive an
   * innerHTML swap of its children; we rebuild the DOM-derived item map afterwards.
   * Best-effort: any failure leaves the client-updated price + totals in place (the
   * pre-existing behaviour), so this can only add correctness, never break the cart.
   * @returns {Promise<boolean>} true if the rows were swapped in
   */
  function _refreshCartSection() {
    if (!_rootEl || !CartAPI || typeof CartAPI.getSections !== 'function') {
      return Promise.resolve(false);
    }
    // Resolve the main-cart section id. Primary: walk UP from the cart root — the
    // items live inside <div id="shopify-section-<key>"> (robust even if the schema
    // class was dropped by a theme-editor re-save). Fallbacks: the schema class, then
    // the known cart.json section key so we always attempt a render.
    var sectionEl = (_rootEl.closest && _rootEl.closest('[id^="shopify-section-"]')) ||
                    document.querySelector('.shopify-section--main-cart');
    var sectionId = (sectionEl && sectionEl.id)
      ? sectionEl.id.replace(/^shopify-section-/, '')
      : 'cart';

    return CartAPI.getSections(sectionId).then(function (res) {
      var html = res && res[sectionId];
      if (typeof html !== 'string' || !html) return false;
      var curItems = _rootEl.querySelector('[data-jrs-cart-items]');
      if (!curItems) return false;
      var doc;
      try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return false; }
      var newItems = doc.querySelector('[data-jrs-cart-items]');
      if (!newItems) return false;
      // Swap ONLY the container's children so the container (and its delegated
      // listeners) survive; then rebuild the DOM-derived item map for the new rows.
      curItems.innerHTML = newItems.innerHTML;
      _itemMap = _buildParentChildMap();

      // [data-jrs-cart-json] lives OUTSIDE [data-jrs-cart-items] (cart-main.liquid),
      // so the innerHTML swap above left it stale. Refresh it from the section
      // render so every consumer of the blob reads the new quantities, and parse it
      // once to hand the fresh cart straight to the bundle-summary enricher.
      var freshCart = null;
      var freshJson = doc.querySelector('[data-jrs-cart-json]');
      if (freshJson) {
        var liveJson = document.querySelector('[data-jrs-cart-json]');
        if (liveJson) liveJson.textContent = freshJson.textContent;
        try { freshCart = JSON.parse(freshJson.textContent); } catch (eJson) { freshCart = null; }
      }

      // The swap dropped the client-rendered bundle manifest: the server ships only
      // the empty [data-jrs-bundle-summary] shell + the raw legacy logo list, so a
      // bundle-parent row would lose its "In this bundle" manifest and re-expose the
      // raw list. Re-run the enricher (idempotent) with the post-change cart so the
      // manifest is rebuilt and the legacy list re-hidden.
      if (JRS.BundleSummary && typeof JRS.BundleSummary.mount === 'function') {
        try { JRS.BundleSummary.mount(freshCart); }
        catch (eBs) { _debugWarn('bundle-summary re-mount after section refresh failed', eBs); }
      }

      _debugLog('cart section re-rendered (discount messaging + bundle manifest refreshed)');
      return true;
    }).catch(function (err) {
      _debugWarn('cart section re-render failed; keeping client-updated rows', err);
      return false;
    });
  }

  /**
   * Recompute the "Bulk buying discount" block on every cart change
   * (2026-09-02 fix — the block was Liquid-rendered once with a baked-in
   * figure and NOTHING recomputed it; it lives in cart-summary, OUTSIDE the
   * [data-jrs-cart-items] container _refreshCartSection() swaps, so this is
   * not the dead-listener class — it is a region that never re-rendered).
   *
   * Value = the governed allocation-agnostic identity settled 2026-09-02:
   *   Σ original_line_price − cart.total_discount = cart.total_price
   * i.e. the saving is original_total_price − total_price (falling back to
   * cart.total_discount) — correct whether the discount allocates at cart or
   * line level (both the position bands and the garment ladder are
   * line-allocated on this store). Hidden entirely at zero — never "−£0.00".
   * The node is re-resolved per call (mirrors the free-delivery-bar fix);
   * the VAT pair keeps its data-price-wrapper spans so the toggle works.
   */
  function _updateBulkSavingsBlock(cart) {
    var block = document.querySelector('[data-cart-bulk-savings]');
    if (!block || !cart) return;
    var bulk = (typeof cart.original_total_price === 'number' && typeof cart.total_price === 'number')
      ? (cart.original_total_price - cart.total_price)
      : (typeof cart.total_discount === 'number' ? cart.total_discount : 0);
    if (!isFinite(bulk) || bulk < 0) bulk = 0;
    if (bulk <= 0) {
      block.setAttribute('hidden', '');
      block.setAttribute('data-jrs-bulk-saving-ex', '0');
      return;
    }
    var fmt = function(pence) {
      var n = pence / 100;
      var s = n.toFixed(2);
      var parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '£' + parts.join('.');
    };
    var exEl = block.querySelector('[data-jrs-bulk-amount-ex]');
    var incEl = block.querySelector('[data-jrs-bulk-amount-inc]');
    if (exEl) exEl.textContent = '-' + fmt(bulk);
    if (incEl) incEl.textContent = '-' + fmt(Math.round(bulk * 1.2));
    block.setAttribute('data-jrs-bulk-saving-ex', String(bulk));
    block.removeAttribute('hidden');
  }

  function _updateCartTotals() {
    if (!_rootEl) return;
    _debugLog('refresh totals requested');

    CartAPI.getCart().then(function(cart) {
      _debugLog('getCart resolved', {
        itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : null,
        totalPrice: cart && typeof cart.total_price === 'number' ? cart.total_price : null
      });
      _applyCartTotals(cart);
      _updateBulkSavingsBlock(cart);
      _cleanupOrphanDiscountWarnings();
    }).catch(function(error) {
      console.error('[CartPage] Failed to update totals', error);
    });
  }

  /**
   * @param {Object} cart
   * @returns {void}
   */
  function _applyCartTotals(cart) {
    if (!_rootEl) return;

    var subtotalEl = _rootEl.querySelector('[data-jrs-subtotal-value-node]') || _rootEl.querySelector('[data-jrs-subtotal-value]');
    var vatEl = _rootEl.querySelector('[data-jrs-vat-value-node]') || _rootEl.querySelector('[data-jrs-vat-value]');
    var logoCostsEl = _rootEl.querySelector('[data-jrs-logo-costs-value]');
    var logoCostsRow = _rootEl.querySelector('[data-jrs-logo-costs-row]');
    var totalIncEl = _rootEl.querySelector('[data-jrs-total-inc-value]');
    var totalExEl = _rootEl.querySelector('[data-jrs-total-ex-value]');
    var totalEl = _rootEl.querySelector('[data-jrs-total-value]');
    var countEl = _rootEl.querySelector('[data-jrs-cart-count]');
    var stickyTotalIncEl = document.querySelector('[data-jrs-sticky-total-inc-value]');
    var stickyTotalExEl = document.querySelector('[data-jrs-sticky-total-ex-value]');
    var stickyTotalEl = document.querySelector('[data-jrs-sticky-total]');

    if (!cart || typeof cart.total_price !== 'number') {
      _debugWarn('skipping totals update: invalid cart payload', cart);
      _ensureEmptyState(cart);
      return;
    }

    var adjustedCount = Number(cart && cart.item_count ? cart.item_count : 0);
    var items = cart && cart.items ? cart.items : [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i] && items[i].properties ? items[i].properties : null;
      if (props && typeof props._ParentVariant !== 'undefined' && props._ParentVariant !== null) {
        adjustedCount -= Number(items[i].quantity || 0);
      }
    }
    if (adjustedCount < 0) adjustedCount = 0;

    var totalsFromCart = _computeTotalsFromCart(cart);
    var totalsFromRows = totalsFromCart ? null : _computeTotalsFromRows();
    var subtotal = totalsFromCart
      ? totalsFromCart.subtotal
      : (totalsFromRows ? totalsFromRows.subtotal : Number(cart.total_price));
    var vat = totalsFromCart
      ? totalsFromCart.vat
      : (totalsFromRows ? totalsFromRows.vat : Math.round(subtotal * 0.2));
    var total = subtotal + vat;
    // "Product Costs" = garments only; the logo/branding lines are shown on their
    // own "Costs To Add Logo" row, so subtract them from the Product Costs figure
    // (the Order Total keeps the full ex-VAT subtotal). Guard against a negative.
    var logoTotal = totalsFromCart
      ? totalsFromCart.logoTotal
      : (totalsFromRows ? totalsFromRows.logoTotal : 0);
    if (!isFinite(logoTotal) || logoTotal < 0) logoTotal = 0;
    var productCosts = subtotal - logoTotal;
    if (!isFinite(productCosts) || productCosts < 0) productCosts = subtotal;
    _debugLog('applying totals', {
      adjustedCount: adjustedCount,
      subtotal: subtotal,
      logoTotal: logoTotal,
      productCosts: productCosts,
      vat: vat,
      total: total,
      hasSubtotalNode: !!subtotalEl,
      hasLogoCostsNode: !!logoCostsEl,
      hasVatNode: !!vatEl,
      hasTotalIncNode: !!totalIncEl,
      hasTotalExNode: !!totalExEl,
      hasCartCountNode: !!countEl
    });

    if (subtotalEl) subtotalEl.textContent = _formatMoney(productCosts);
    if (logoCostsEl) logoCostsEl.textContent = _formatMoney(logoTotal);
    if (logoCostsRow) {
      if (logoTotal > 0) {
        logoCostsRow.removeAttribute('hidden');
        logoCostsRow.classList.remove('hidden');
      } else {
        logoCostsRow.setAttribute('hidden', '');
        logoCostsRow.classList.add('hidden');
      }
    }
    if (vatEl) vatEl.textContent = _formatMoney(vat);
    if (totalIncEl) totalIncEl.textContent = _formatMoney(total);
    if (totalExEl) totalExEl.textContent = _formatMoney(subtotal);
    if (!totalIncEl && !totalExEl && totalEl) {
      // Fallback for older markup where only wrapper hook exists.
      totalEl.textContent = _formatMoney(total);
    }
    if (countEl) {
      countEl.textContent = adjustedCount > 0 ? '(' + adjustedCount + ')' : '';
    }
    var countRawEl = document.querySelector('[data-cart-count]');
    if (countRawEl) countRawEl.textContent = String(adjustedCount);
    var titleCountEl = document.querySelector('[data-cart-title-count]');
    if (titleCountEl) titleCountEl.textContent = adjustedCount > 0 ? '(' + adjustedCount + ')' : '';
    var summaryCountNodes = document.querySelectorAll('.cart_count');
    for (var j = 0; j < summaryCountNodes.length; j++) {
      summaryCountNodes[j].textContent = adjustedCount > 0 ? '(' + adjustedCount + ')' : '';
    }
    if (stickyTotalIncEl) stickyTotalIncEl.textContent = _formatMoney(total);
    if (stickyTotalExEl) stickyTotalExEl.textContent = _formatMoney(subtotal);
    if (!stickyTotalIncEl && !stickyTotalExEl && stickyTotalEl) {
      // Fallback for older markup where only wrapper hook exists.
      stickyTotalEl.textContent = _formatMoney(total);
    }

    _syncCustomizationPricesFromCart(cart);
    _ensureEmptyState(cart);
  }

  /**
   * Compute subtotal/vat/total from rendered parent rows.
   * This preserves VAT-free behavior using `data-jrs-item-vat-free`.
   *
   * @returns {{ subtotal: number, vat: number, total: number }|null}
   */
  function _computeTotalsFromRows() {
    if (!_rootEl) return null;
    var rows = _rootEl.querySelectorAll('[data-jrs-cart-item]');
    if (!rows || rows.length === 0) return null;

    var subtotal = 0;
    var taxableSubtotal = 0;
    var logoTotal = 0;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row || row.style.display === 'none') continue;

      var linePrice = Number(
        row.getAttribute('data-jrs-line-price') ||
        row.getAttribute('data-jrs-item-line-price') ||
        0
      );
      if (!isFinite(linePrice) || linePrice < 0) linePrice = 0;
      subtotal += linePrice;

      // Logo/branding lines (position variants) bill separately as "Costs To Add
      // Logo" — track them so "Product Costs" can exclude them.
      if (String(row.getAttribute('data-is-position-variant') || '').toLowerCase() === 'true') {
        logoTotal += linePrice;
      }

      var isVatFree = String(row.getAttribute('data-jrs-item-vat-free') || '').toLowerCase() === 'true';
      if (!isVatFree) {
        taxableSubtotal += linePrice;
      }
    }

    var vat = Math.round(taxableSubtotal * 0.2);
    return {
      subtotal: subtotal,
      logoTotal: logoTotal,
      vat: vat,
      total: subtotal + vat
    };
  }

  /**
   * Compute subtotal/vat/total from fresh cart payload.
   * This keeps summary totals in sync with child logo/customization lines.
   *
   * @param {Object} cart
   * @returns {{ subtotal: number, vat: number, total: number }|null}
   */
  function _computeTotalsFromCart(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;

    var subtotal = 0;
    var taxableSubtotal = 0;
    var logoTotal = 0;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i] || {};
      var linePrice = Number(
        typeof item.final_line_price !== 'undefined' ? item.final_line_price : item.line_price
      );
      if (!isFinite(linePrice) || linePrice < 0) linePrice = 0;
      subtotal += linePrice;

      // Logo/branding lines (position products) bill separately as "Costs To Add
      // Logo" — track them so "Product Costs" can exclude them. Identified by the
      // customization-position product type OR the _ParentVariant branding-child
      // marker (mirrors the row path's data-is-position-variant).
      var props = item.properties || {};
      var isLogoLine = item.product_type === 'customization-position' ||
        (props._ParentVariant != null && props._ParentVariant !== '');
      if (isLogoLine) {
        logoTotal += linePrice;
      }

      var isTaxable = typeof item.taxable === 'boolean' ? item.taxable : true;
      if (isTaxable) {
        taxableSubtotal += linePrice;
      }
    }

    var vat = Math.round(taxableSubtotal * 0.2);
    return {
      subtotal: subtotal,
      logoTotal: logoTotal,
      vat: vat,
      total: subtotal + vat
    };
  }

  /**
   * Keep inline customization/logo prices in sync with latest cart payload.
   * Targets legacy hooks rendered in `logo-cart-item.liquid`.
   *
   * @param {Object} cart
   * @returns {void}
   */
  function _syncCustomizationPricesFromCart(cart) {
    if (!_rootEl || !cart || !Array.isArray(cart.items)) return;

    var byKey = {};
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!item || !item.key) continue;
      byKey[String(item.key)] = item;
    }

    var linePriceNodes = _rootEl.querySelectorAll('[data-hulkapps-lineitem][data-key]');
    for (var j = 0; j < linePriceNodes.length; j++) {
      var lineNode = linePriceNodes[j];
      if (!lineNode) continue;
      var lineKey = String(lineNode.getAttribute('data-key') || '').trim();
      if (!lineKey) continue;
      var lineItem = _resolveCustomizationLineItem(lineNode, lineKey, byKey, cart.items);
      if (!lineItem) continue;

      var lineEx = Number(
        typeof lineItem.final_line_price !== 'undefined' ? lineItem.final_line_price : lineItem.line_price
      );
      if (!isFinite(lineEx) || lineEx < 0) lineEx = 0;
      var isTaxable = typeof lineItem.taxable === 'boolean' ? lineItem.taxable : true;
      var lineInc = isTaxable ? Math.round(lineEx * 1.2) : lineEx;

      var exNode = lineNode.querySelector('.price.ex-vat .u_pro_f_price');
      var incNode = lineNode.querySelector('.price.in-vat .u_pro_f_price');
      if (exNode) exNode.textContent = _formatMoney(lineEx);
      if (incNode) incNode.textContent = _formatMoney(lineInc);

      var badge = lineNode.parentElement ? lineNode.parentElement.querySelector('.logo-discount-badge-custom') : null;
      if (badge) {
        var hasDiscount = Number(lineItem.original_line_price || 0) > lineEx;
        badge.style.display = hasDiscount ? '' : 'none';
      }

      var unitCostContainer = _findLogoUnitCostContainer(lineNode);
      if (unitCostContainer) {
        unitCostContainer.style.display = 'none';
      }
    }
  }

  /**
   * Resolve customization line item even when Shopify rotated line keys.
   *
   * @param {Element} lineNode
   * @param {string} lineKey
   * @param {Object<string, Object>} byKey
   * @param {Array<Object>} items
   * @returns {Object|null}
   */
  function _resolveCustomizationLineItem(lineNode, lineKey, byKey, items) {
    if (lineKey && byKey[lineKey]) {
      return byKey[lineKey];
    }

    var parentRow = lineNode.closest('[data-jrs-cart-item]');
    if (!parentRow || !Array.isArray(items)) return null;

    var parentKey = String(parentRow.getAttribute('data-jrs-item-key') || '').trim();
    var parentVariant = Number(parentRow.getAttribute('data-jrs-item-variant-id') || 0);
    var variantFromKey = Number(String(lineKey || '').split(':')[0] || 0);

    var methodHint = '';
    var positionHint = '';
    var itemRow = lineNode.closest('.ItemCustomisations-customisationsItem-1jn');
    if (itemRow) {
      var methodEl = itemRow.querySelector('.ItemCustomisations-customisationsInlineIconsAndMethodWrapper-1Rj div');
      methodHint = String(methodEl && methodEl.textContent ? methodEl.textContent : '').trim().toLowerCase();
      var posEl = itemRow.querySelector('.ItemCustomisations-customisationsLocation-1BV');
      positionHint = String(posEl && posEl.textContent ? posEl.textContent : '')
        .replace(/^\s*Position\s*:\s*/i, '')
        .trim()
        .toLowerCase();
    }

    // Row scope (2026-08-02): resolve the parent line to its
    // _customization_instance_id so duplicate same-variant rows can't
    // cross-match children.
    var parentScopeCid = '';
    if (parentKey) {
      for (var ps = 0; ps < items.length; ps++) {
        if (items[ps] && String(items[ps].key || '') === String(parentKey)) {
          parentScopeCid = String((items[ps].properties || {})._customization_instance_id || '');
          break;
        }
      }
    }
    var candidates = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.properties) continue;

      if (parentScopeCid && item.properties._customization_instance_id &&
          String(item.properties._customization_instance_id) !== parentScopeCid) continue;

      var itemVariantId = Number(item.variant_id || 0);
      if (variantFromKey > 0 && itemVariantId > 0 && itemVariantId !== variantFromKey) continue;

      var itemParentKey = String(item.properties._ParentKey || '').trim();
      var itemParentVariant = Number(item.properties._ParentVariant || 0);
      var linksToParent = (
        (parentKey && itemParentKey && itemParentKey === parentKey) ||
        (parentVariant > 0 && itemParentVariant > 0 && itemParentVariant === parentVariant)
      );
      if (!linksToParent) continue;

      candidates.push(item);
    }

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // Refine match with method + position if available.
    for (var j = 0; j < candidates.length; j++) {
      var c = candidates[j];
      var cMethod = String((c.properties && _dualProp(c.properties, 'Application Method')) || '').trim().toLowerCase();
      var cTitle = String(c.product_title || c.title || '').trim().toLowerCase();
      if (methodHint && cMethod && cMethod !== methodHint) continue;
      if (positionHint && cTitle && cTitle.indexOf(positionHint) === -1 && positionHint.indexOf(cTitle) === -1) continue;
      return c;
    }

    return candidates[0];
  }

  /**
   * @param {Element} lineNode
   * @returns {Element|null}
   */
  function _findLogoUnitCostContainer(lineNode) {
    if (!lineNode) return null;
    var wrap = lineNode.closest('.ItemCustomisations-customisationsActionsRightAndPriceWrapper-3fe');
    if (!wrap) return null;
    return wrap.querySelector('.mrk-single-logo-cost');
  }

  /**
   * Remove discount warning that belongs to a specific cart row.
   *
   * @param {Element} rowEl
   * @returns {void}
   */
  function _removeDiscountWarningForRow(rowEl) {
    if (!rowEl || !_rootEl) return;
    var prev = rowEl.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('discount-warn-custom')) {
      prev.parentNode && prev.parentNode.removeChild(prev);
    }
  }

  /**
   * Remove warning banners that are no longer attached to a cart item.
   *
   * @returns {void}
   */
  function _cleanupOrphanDiscountWarnings() {
    if (!_rootEl) return;
    var warnings = _rootEl.querySelectorAll('.discount-warn-custom');
    for (var i = 0; i < warnings.length; i++) {
      var warning = warnings[i];
      if (!warning) continue;
      var next = warning.nextElementSibling;
      var hasAttachedRow = !!(next && next.matches && next.matches('[data-jrs-cart-item]'));
      if (!hasAttachedRow) {
        warning.parentNode && warning.parentNode.removeChild(warning);
      }
    }
  }

  /**
   * @param {Object|null|undefined} cart
   * @returns {boolean}
   */
  function _isCartNowEmpty(cart) {
    if (!cart || typeof cart !== 'object') return false;
    if (typeof cart.item_count === 'number') {
      return cart.item_count <= 0;
    }
    if (Array.isArray(cart.items)) {
      return cart.items.length === 0;
    }
    return false;
  }

  function _bindLegacyCartEvents() {
    if (typeof document === 'undefined') return;
    var onDocumentCartUpdated = function(event) {
      var detail = event && event.detail ? event.detail : null;
      var payloadCart = detail && detail.cart ? detail.cart : detail;
      _debugLog('document cart event', {
        eventType: event && event.type ? event.type : null,
        hasDetail: !!detail,
        hasPayloadCart: !!(payloadCart && typeof payloadCart === 'object'),
        payloadHasTotal: !!(payloadCart && typeof payloadCart.total_price === 'number')
      });
      if (payloadCart && typeof payloadCart.total_price === 'number') {
        _applyCartTotals(payloadCart);
        return;
      }
      _updateCartTotals();
    };

    var events = ['cart:updated', 'cart:refresh', 'cart:change'];
    for (var i = 0; i < events.length; i++) {
      document.addEventListener(events[i], onDocumentCartUpdated);
      _boundDocEvents.push({ event: events[i], handler: onDocumentCartUpdated });
    }
  }

  function _destroyDocEventSubscriptions() {
    if (typeof document === 'undefined') return;
    for (var i = 0; i < _boundDocEvents.length; i++) {
      var item = _boundDocEvents[i];
      if (!item) continue;
      document.removeEventListener(item.event, item.handler);
    }
    _boundDocEvents = [];
  }

  /**
   * @param {Object} [cart]
   * @returns {void}
   */
  function _ensureEmptyState(cart) {
    if (!_rootEl) return;

    var rowEls = _rootEl.querySelectorAll('[data-jrs-cart-item]');
    var hasVisibleRows = false;
    for (var i = 0; i < rowEls.length; i++) {
      var row = rowEls[i];
      if (!row) continue;
      if (row.style && row.style.display === 'none') continue;
      hasVisibleRows = true;
      break;
    }
    var isCartEmpty = false;

    if (cart && typeof cart.item_count === 'number') {
      isCartEmpty = cart.item_count === 0;
    } else if (_cartData && typeof _cartData.item_count === 'number') {
      isCartEmpty = _cartData.item_count === 0;
    } else {
      isCartEmpty = !hasVisibleRows;
    }

    if (isCartEmpty || !hasVisibleRows) {
      _showCartEmpty();
    }
  }

  function _showCartEmpty() {
    if (!_rootEl) return;
    var itemsContainer = _rootEl.querySelector('[data-jrs-cart-items]');
    var emptyContainer = _rootEl.querySelector('[data-jrs-cart-empty]');
    var summaryContainer = _rootEl.querySelector('[data-jrs-cart-summary]');
    var stickyBar = _rootEl.querySelector('[data-jrs-sticky-checkout]');
    var checkoutBtns = _rootEl.querySelectorAll('[data-jrs-checkout-btn], [data-jrs-sticky-checkout-btn]');

    if (itemsContainer) itemsContainer.style.display = 'none';
    if (emptyContainer) emptyContainer.style.display = '';
    if (summaryContainer) summaryContainer.style.display = 'none';
    if (stickyBar) stickyBar.style.display = 'none';

    for (var i = 0; i < checkoutBtns.length; i++) {
      checkoutBtns[i].setAttribute('disabled', '');
    }
  }

  /**
   * @param {string} message
   * @param {'success'|'error'|'info'} type
   * @returns {void}
   */
  function _showNotification(message, type) {
    if (!_rootEl) return;
    var notice = _rootEl.querySelector('[data-jrs-cart-notification]');
    if (!notice) return;
    var messageEl = notice.querySelector('[data-jrs-cart-notification-message]');

    if (messageEl) {
      messageEl.textContent = String(message || '');
    } else {
      notice.textContent = String(message || '');
    }
    notice.className = 'jrs-cart-notification jrs-cart-notification--' + String(type || 'info');
    notice.style.display = '';

    if (type !== 'error') {
      setTimeout(function() {
        if (!notice) return;
        notice.style.display = 'none';
      }, 5000);
    }
  }

  /**
   * @param {Element|Window|Document} el
   * @param {string} event
   * @param {function} handler
   * @returns {void}
   */
  function _bindListener(el, event, handler) {
    if (!el || !event || !handler) return;
    el.addEventListener(event, handler);
    _boundListeners.push({
      el: el,
      event: event,
      handler: handler
    });
  }

  function _destroyListeners() {
    for (var i = 0; i < _boundListeners.length; i++) {
      var item = _boundListeners[i];
      if (!item || !item.el) continue;
      item.el.removeEventListener(item.event, item.handler);
    }
    _boundListeners = [];
  }

  function _destroyEventSubscriptions() {
    for (var i = 0; i < _eventUnsubs.length; i++) {
      var unsub = _eventUnsubs[i];
      if (typeof unsub !== 'function') continue;
      try {
        unsub();
      } catch (error) {
        console.error('[CartPage] Failed to unsubscribe event', error);
      }
    }
    _eventUnsubs = [];
  }

  function _clearQtyInputTimers() {
    var keys = Object.keys(_qtyInputTimers);
    for (var i = 0; i < keys.length; i++) {
      clearTimeout(_qtyInputTimers[keys[i]]);
    }
    _qtyInputTimers = {};
  }

  /**
   * @param {Element} rowEl
   * @returns {void}
   */
  function _clearQtyTimerForRow(rowEl) {
    if (!rowEl) return;
    var rowKey = rowEl.getAttribute('data-jrs-item-key') || '';
    _clearQtyTimerForKey(rowKey);
  }

  /**
   * @param {string} rowKey
   * @returns {void}
   */
  function _clearQtyTimerForKey(rowKey) {
    if (!rowKey) return;
    if (_qtyInputTimers[rowKey]) {
      clearTimeout(_qtyInputTimers[rowKey]);
      delete _qtyInputTimers[rowKey];
    }
  }

  /**
   * @param {Element} rowEl
   * @param {number} prevQty
   * @param {number} unitPrice
   * @returns {void}
   */
  function _revertQuantityUI(rowEl, prevQty, unitPrice) {
    if (!rowEl) return;
    var input = rowEl.querySelector('[data-jrs-qty-input]');
    if (input) input.value = String(prevQty);
    rowEl.style.display = '';
    // The change failed, so the server is still at prevQty and the row's
    // server-rendered data-jrs-item-line-price still reflects it — pass it as the
    // explicit total so a discounted line reverts to its true price, not unit×qty.
    var serverTotal = Number(rowEl.getAttribute('data-jrs-item-line-price'));
    _updateLinePrice(
      rowEl,
      unitPrice,
      prevQty,
      isFinite(serverTotal) ? serverTotal : undefined
    );
  }

  // Debounced validateCart trigger. Called after every successful qty change so
  // any drift produced by the qty-stepper chain (Bug: bundle paid-position
  // children stranded at intermediate values) is auto-healed within ~300 ms.
  // The debounce coalesces a burst of clicks into a single validation pass.
  var _driftValidationTimer = null;
  // The most-recently-changed parent row + its stable match context, captured so
  // the post-heal repaint can re-sync THAT row's qty input + price from server
  // truth. The 300 ms debounce coalesces a burst of clicks on one row, so the
  // last-captured row is the one on screen.
  var _driftPendingRepaint = null;
  function _runDriftValidation(rowEl, matchCtx) {
    if (!window.JRS || !JRS.CartValidator || typeof JRS.CartValidator.validateCart !== 'function') return;
    if (rowEl) _driftPendingRepaint = { rowEl: rowEl, ctx: matchCtx || {} };
    if (_driftValidationTimer) clearTimeout(_driftValidationTimer);
    _driftValidationTimer = setTimeout(function() {
      _driftValidationTimer = null;
      var pending = _driftPendingRepaint;
      _driftPendingRepaint = null;
      try {
        JRS.CartValidator.validateCart().then(function(result) {
          if (result && result.issuesFixed > 0) {
            _debugLog('drift validation healed cart', {
              issuesFound: result.issues ? result.issues.length : 0,
              issuesFixed: result.issuesFixed
            });
            // Previously window.location.reload(). The reload's one virtue was
            // showing server truth; its cost was a full-page repaint on every
            // bundle qty change. Repaint IN PLACE from POST-HEAL truth instead.
            // This runs INSIDE the heal's .then(), so /cart.js is read only AFTER
            // the heal's re-adds have landed — never a pre-heal read. Re-syncs the
            // changed parent row's qty input + line price + cart totals so the DOM
            // matches the healed cart without a reload.
            _repaintAfterHeal(pending ? pending.rowEl : null, pending ? pending.ctx : null);
          }
        }).catch(function(err) {
          _debugWarn('drift validation failed', { error: (err && err.message) || String(err) });
        });
      } catch (e) {
        _debugWarn('drift validation threw', { error: e && e.message });
      }
    }, 300);
  }

  // In-place repaint after a drift heal — the no-reload replacement. Reads
  // POST-heal server truth (caller guarantees this runs after the heal resolved),
  // refreshes _cartData + totals, and re-syncs the changed parent row's qty input
  // value + line price so the user-facing number matches the healed cart. On a
  // refetch failure we deliberately do NOT fall back to reload: the heal already
  // corrected the server cart, and a stale display self-heals on the next action.
  function _repaintAfterHeal(rowEl, ctx) {
    return CartAPI.getCart().then(function(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      _cartData = cart;                 // keep state fresh for the next click
      _applyCartTotals(cart);           // totals from server truth (guards on _rootEl)
      _cleanupOrphanDiscountWarnings();
      _resyncParentRow(rowEl, ctx, cart);
    }).catch(function(error) {
      _debugWarn('post-heal repaint failed', { error: (error && error.message) || String(error) });
    });
  }

  // Re-sync one parent row's displayed qty + price from the refetched (post-heal)
  // cart. Shopify rewrites line keys on qty/heal changes, so the fresh item is
  // matched by a STABLE id — bundle parents by _bundle_instance_id (+ variant),
  // mirroring the onSuccess key-refresh — never by the stale DOM key.
  function _resyncParentRow(rowEl, ctx, cart) {
    if (!rowEl || !ctx || !cart || !Array.isArray(cart.items)) return;
    var fresh = null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !it.properties) continue;
      if (it.properties._ParentVariant) continue; // skip bundle children
      if (Number(it.variant_id || 0) !== Number(ctx.variantId || 0)) continue;
      if (ctx.isBundleParent && ctx.instanceId) {
        if (String(it.properties._bundle_instance_id || '') !== String(ctx.instanceId)) continue;
      }
      fresh = it;
      break;
    }
    if (!fresh) return;

    // Refresh the (possibly heal-rewritten) DOM key + _itemMap so the next click
    // keys correctly — same contract as onSuccess.
    var freshKey = String(fresh.key || '');
    var oldKey = rowEl.getAttribute('data-jrs-item-key') || '';
    if (freshKey && freshKey !== oldKey) {
      rowEl.setAttribute('data-jrs-item-key', freshKey);
      if (_itemMap && _itemMap.byParentKey && _itemMap.byParentKey[oldKey]) {
        _itemMap.byParentKey[freshKey] = _itemMap.byParentKey[oldKey];
        delete _itemMap.byParentKey[oldKey];
      }
    }

    // The number the user stares at: input value + line price, both from truth.
    // Pass the refetched final_line_price so a bulk-discounted parent paints its
    // true discounted total, not unit × qty (which _updateLinePrice would refuse
    // to fabricate over a discounted line anyway).
    var freshQty = Number(fresh.quantity || 0);
    var freshTotal = Number(fresh.final_line_price);
    var input = rowEl.querySelector('[data-jrs-qty-input]');
    if (input) {
      input.value = String(freshQty);
      input.defaultValue = String(freshQty);
    }
    _updateLinePrice(
      rowEl,
      _readRowUnitPrice(rowEl),
      freshQty,
      isFinite(freshTotal) ? freshTotal : undefined
    );
  }

  /**
   * @param {Element} rowEl
   * @param {boolean} isLoading
   * @returns {void}
   */
  /**
   * Collapses (or restores) a cart row while its removal is in flight.
   * @param {Element} rowEl
   * @param {boolean} isCollapsed
   * @returns {void}
   */
  function _setRowCollapsed(rowEl, isCollapsed) {
    if (!rowEl || !rowEl.style) return;
    rowEl.style.display = isCollapsed ? 'none' : '';
  }

  function _setRowLoading(rowEl, isLoading) {
    if (!rowEl) return;
    _debugLog('row loading state', {
      itemKey: rowEl.getAttribute('data-jrs-item-key') || null,
      isLoading: !!isLoading
    });
    if (isLoading) {
      rowEl.classList.add('is-loading');
    } else {
      rowEl.classList.remove('is-loading');
    }
    _toggleFullCartLoader();
  }

  /**
   * @returns {void}
   */
  function _toggleFullCartLoader() {
    if (!_rootEl) return;
    var overlay = _rootEl.querySelector('.full-cart-loader--overlay');
    if (!overlay) return;
    var hasLoadingRows = _rootEl.querySelector('.mrk-cart-item.is-loading, [data-jrs-cart-item].is-loading') !== null;
    if (hasLoadingRows) {
      overlay.classList.add('full-cart-loader--visible');
      overlay.style.display = 'flex';
      overlay.setAttribute('aria-hidden', 'false');
      return;
    }

    overlay.classList.remove('full-cart-loader--visible');
    overlay.style.display = 'none';
    overlay.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {HTMLInputElement|Element} inputEl
   * @returns {number}
   */
  function _readMOQ(inputEl) {
    return Number(inputEl.getAttribute('data-moq') || 0);
  }

  /**
   * @param {HTMLInputElement|Element} inputEl
   * @param {number} qty
   * @returns {number}
   */
  function _normalizeCurrentQty(inputEl, qty) {
    return _enforceMOQ(inputEl, qty);
  }

  /**
   * @param {Element} rowEl
   * @returns {number}
   */
  function _readRowUnitPrice(rowEl) {
    return Number(
      rowEl.getAttribute('data-jrs-item-unit-price') ||
      rowEl.getAttribute('data-jrs-unit-price') ||
      0
    );
  }

  /**
   * @param {Element} rowEl
   * @returns {number}
   */
  function _readRowVariantId(rowEl) {
    return Number(
      rowEl.getAttribute('data-jrs-item-variant-id') ||
      rowEl.getAttribute('data-jrs-variant-id') ||
      0
    );
  }

  /**
   * Find a cart line item by its stable `_lineItemKey` custom property.
   * Shopify regenerates the `item.key` on qty changes, but `_lineItemKey` is
   * a custom property set at add-time and stays stable across cart mutations.
   *
   * @param {Object|null} cart
   * @param {string} lineItemKey
   * @returns {Object|null}
   */
  function _findItemByLineItemKey(cart, lineItemKey) {
    if (!cart || !Array.isArray(cart.items) || !lineItemKey) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !it.properties) continue;
      if (String(it.properties._lineItemKey || '') === String(lineItemKey)) return it;
    }
    return null;
  }

  // Resolve ONE bundle child line by the composite (_lineItemKey, _ProductVariantId).
  // Per-size children of a position share both _lineItemKey AND cart variant_id
  // (id: positionVariantId), so _findItemByLineItemKey alone returns the first
  // match and collapses the sizes; _ProductVariantId (the garment variant) is the
  // discriminator. When no variant is supplied it degrades to a _lineItemKey-only
  // match (single-size positions / callers without the property). Both fields are
  // cart-line properties, so they survive Shopify's per-change.js key rewrites.
  function _findChildLine(cart, lineItemKey, productVariantId) {
    if (!cart || !Array.isArray(cart.items) || !lineItemKey) return null;
    var pv = String(productVariantId || '');
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !it.properties) continue;
      if (String(it.properties._lineItemKey || '') !== String(lineItemKey)) continue;
      if (pv && String(it.properties._ProductVariantId || '') !== pv) continue;
      return it;
    }
    return null;
  }

  /**
   * D-1 step 2 (Bug F / ROOT-1): resolve a parent row's child lines LIVE by their
   * stable `_lineItemKey`, scoped by `_bundle_instance_id` (bundle) / `_ParentKey`
   * (customized parent) — NEVER the page-load `data-jrs-child-keys` list (stale after
   * any prior mutation, and variant-collapsing). This is the removal-cascade twin of
   * the qty path's already-correct child sourcing (`_onQuantityChange` sequential
   * branch); same scoping rules, but it returns `_lineItemKey`s for
   * `CartAPI.removeItemsByLineItemKey`. Multi-bundle safe: two bundles of the same
   * parent variant return only the clicked instance's children.
   *
   * @param {Object} cart        live cart (from CartAPI.getCart)
   * @param {string} parentKey   the clicked parent's current Shopify line key
   * @param {boolean} isBundleParent
   * @param {boolean} isCustomizedParent
   * @returns {string[]} child `_lineItemKey`s belonging to this parent
   */
  function _resolveChildLineItemKeysLive(cart, parentKey, isBundleParent, isCustomizedParent) {
    var keys = [];
    if (!cart || !Array.isArray(cart.items)) return keys;

    var parentItem = null;
    for (var pi = 0; pi < cart.items.length; pi++) {
      if (cart.items[pi] && String(cart.items[pi].key || '') === String(parentKey)) {
        parentItem = cart.items[pi];
        break;
      }
    }
    if (!parentItem) return keys;

    var parentVariantId = Number(parentItem.variant_id || 0);
    if (!parentVariantId) return keys;
    var parentInstanceId = (isBundleParent && parentItem.properties)
      ? String(parentItem.properties._bundle_instance_id || '')
      : '';
    var parentCustomInstanceId = (!isBundleParent && parentItem.properties)
      ? String(parentItem.properties._customization_instance_id || '')
      : '';

    for (var bi = 0; bi < cart.items.length; bi++) {
      var cItem = cart.items[bi];
      if (!cItem || !cItem.properties) continue;
      if (Number(cItem.properties._ParentVariant || 0) !== parentVariantId) continue;

      if (isBundleParent) {
        // Scope to this bundle instance when both sides carry the id; fall through
        // to variant-only only when an id is absent (mirrors the qty path).
        if (parentInstanceId && cItem.properties._bundle_instance_id) {
          if (String(cItem.properties._bundle_instance_id) !== parentInstanceId) continue;
        }
      } else {
        // Row-scope id first (2026-08-02), then _ParentKey when the child carries one.
        if (parentCustomInstanceId && cItem.properties._customization_instance_id &&
            String(cItem.properties._customization_instance_id) !== parentCustomInstanceId) continue;
        var cpk = cItem.properties._ParentKey ? String(cItem.properties._ParentKey) : '';
        if (cpk && cpk !== String(parentKey)) continue;
      }

      var lik = String(cItem.properties._lineItemKey || '');
      if (lik) keys.push(lik);
    }
    return keys;
  }

  /**
   * D-1 step 2: re-resolve the parent's current Shopify line key against the freshest
   * cart. The page-load key usually stays valid (the parent variant differs from its
   * service-variant children, so removing children doesn't re-key it), but if it has
   * gone, fall back to a signature match (variant + `_bundle_instance_id` for bundles)
   * rather than firing a stale key.
   *
   * @returns {string} the parent's current key, or fallbackKey if unresolved
   */
  function _resolveParentKeyLive(cart, fallbackKey, parentVariantId, parentInstanceId, isBundleParent) {
    if (!cart || !Array.isArray(cart.items)) return fallbackKey;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i] && String(cart.items[i].key || '') === String(fallbackKey)) return fallbackKey;
    }
    if (parentVariantId) {
      for (var j = 0; j < cart.items.length; j++) {
        var it = cart.items[j];
        if (!it) continue;
        if (it.properties && it.properties._ParentVariant) continue; // skip children
        if (Number(it.variant_id || 0) !== Number(parentVariantId)) continue;
        if (isBundleParent && parentInstanceId && it.properties && it.properties._bundle_instance_id) {
          if (String(it.properties._bundle_instance_id) !== String(parentInstanceId)) continue;
        }
        return String(it.key || fallbackKey);
      }
    }
    return fallbackKey;
  }

  /**
   * @param {string} parentKey
   * @param {Element} rowEl
   * @returns {string[]}
   */
  function _getChildKeysForParent(parentKey, rowEl) {
    if (_itemMap && _itemMap.byParentKey && _itemMap.byParentKey[parentKey]) {
      return _itemMap.byParentKey[parentKey].slice();
    }
    if (!rowEl) return [];
    return _parseChildKeys(rowEl.getAttribute('data-jrs-child-keys') || '');
  }

  /**
   * Resolve child quantity ratios for a parent row.
   * Priority:
   * 1) bundle _bundle_raw mapping (Logo Id + Position + Application Method)
   * 2) data-childqtymap on qty input
   * 3) key~qty in data-childitems
   * 4) default ratio 1
   *
   * @param {string} parentKey
   * @param {Element} rowEl
   * @param {string[]} childKeys
   * @returns {Object<string, number>}
   */
  function _getChildQtyRatiosForParent(parentKey, rowEl, childKeys) {
    var ratios = {};
    var keys = childKeys || [];
    if (!rowEl || keys.length === 0) return ratios;

    var inputEl = rowEl.querySelector('[data-jrs-qty-input]');
    var ratioFromMap = _parseChildQtyMap(inputEl ? inputEl.getAttribute('data-childqtymap') : '');
    var ratioFromItems = _parseChildItemsRatioMap(inputEl ? (inputEl.getAttribute('data-childitems') || inputEl.getAttribute('childitems')) : '');

    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      ratios[key] = ratioFromMap[key] || ratioFromItems[key] || 1;
    }

    var itemType = rowEl.getAttribute('data-jrs-item-type') || '';
    if (itemType !== 'bundle-parent' || !_cartData || !Array.isArray(_cartData.items)) {
      return ratios;
    }

    var parentItem = null;
    var childByKey = {};
    for (var ci = 0; ci < _cartData.items.length; ci++) {
      var item = _cartData.items[ci];
      if (!item) continue;
      if (String(item.key || '') === String(parentKey)) {
        parentItem = item;
      }
      childByKey[String(item.key || '')] = item;
    }
    if (!parentItem) return ratios;

    for (var k = 0; k < keys.length; k++) {
      var childKey = keys[k];
      var childItem = childByKey[String(childKey)];
      var bundleRatio = _getBundleBaseQtyForChild(parentItem, childItem);
      if (bundleRatio != null && bundleRatio > 0) {
        ratios[childKey] = bundleRatio;
      }
    }

    return ratios;
  }

  /**
   * @param {string|null} raw
   * @returns {Object<string, number>}
   */
  function _parseChildQtyMap(raw) {
    var out = {};
    var value = String(raw || '').trim();
    if (!value) return out;
    var parts = value.split(',');
    for (var i = 0; i < parts.length; i++) {
      var token = String(parts[i] || '').trim();
      if (!token) continue;
      var seg = token.split('~');
      var key = String(seg[0] || '').trim();
      if (!key) continue;
      var qty = seg.length > 1 ? Number(seg[1]) : 1;
      if (!isFinite(qty) || qty <= 0) qty = 1;
      out[key] = qty;
    }
    return out;
  }

  /**
   * @param {string|null} raw
   * @returns {Object<string, number>}
   */
  function _parseChildItemsRatioMap(raw) {
    var out = {};
    var value = String(raw || '').trim();
    if (!value) return out;
    var parts = value.split(',');
    for (var i = 0; i < parts.length; i++) {
      var token = String(parts[i] || '').trim();
      if (!token) continue;
      var seg = token.split('~');
      var key = String(seg[0] || '').trim();
      if (!key) continue;
      var qty = seg.length > 1 ? Number(seg[1]) : 1;
      if (!isFinite(qty) || qty <= 0) qty = 1;
      out[key] = qty;
    }
    return out;
  }

  /**
   * @param {Object|null} parentItem
   * @param {Object|null} childItem
   * @returns {number|null}
   */
  function _getBundleBaseQtyForChild(parentItem, childItem) {
    if (!parentItem || !parentItem.properties || !parentItem.properties._bundle_raw || !childItem) return null;
    var raw = parentItem.properties._bundle_meta || parentItem.properties._bundle_raw; // meta-first (2026-08-20)
    var rawData = null;
    try {
      rawData = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      return null;
    }
    var bundleItems = _toArrayValues(rawData);
    if (!bundleItems.length) return null;

    var childProps = childItem.properties || {};
    var childLogoId = String(_dualProp(childProps, 'Logo Id') || '');
    var childPosition = String(childProps.Position || childProps.position || '').trim();
    var childMethod = String(_dualProp(childProps, 'Application Method') || '').trim();

    for (var i = 0; i < bundleItems.length; i++) {
      var positions = _toArrayValues(bundleItems[i] && bundleItems[i].positions ? bundleItems[i].positions : []);
      for (var j = 0; j < positions.length; j++) {
        var pos = positions[j] || {};
        var posLogoId = String(pos['Logo Id'] || pos.logoId || '');
        var posName = String(pos.name || pos.Position || '').trim();
        var posMethod = String(pos['Application Method'] || pos.applicationMethod || '').trim();
        if (posLogoId !== childLogoId) continue;
        if (posName && childPosition && posName !== childPosition) continue;
        if (posMethod && childMethod && posMethod !== childMethod) continue;
        var qty = Number(pos.qty);
        if (isFinite(qty) && qty > 0) return qty;
      }
    }
    return null;
  }

  /**
   * @param {Object|Array|null} input
   * @returns {Array}
   */
  function _toArrayValues(input) {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input !== 'object') return [];
    var out = [];
    for (var key in input) {
      if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
      out.push(input[key]);
    }
    return out;
  }

  /**
   * @param {number} pence
   * @returns {string}
   */
  function _formatMoney(pence) {
    return '£' + (Number(pence || 0) / 100).toFixed(2);
  }

  JRS.CartPage = {
    init: init,
    destroy: destroy,
    // D-1 step 2: exposed for unit testing the multi-bundle removal scoping.
    _resolveChildLineItemKeysLive: _resolveChildLineItemKeysLive,
    _resolveParentKeyLive: _resolveParentKeyLive,
    // Exposed for the no-reload regression lock: after a bundle qty change that
    // heals drift, _runDriftValidation must repaint in place (refetch /cart.js +
    // re-sync the row) — never window.location.reload().
    _runDriftValidation: _runDriftValidation,
    _repaintAfterHeal: _repaintAfterHeal,
    // Exposed for the multisize-scale regression lock: the qty handler must scale
    // every per-size child line (resolved by composite _lineItemKey +
    // _ProductVariantId), not collapse them to the first match.
    _onQuantityChange: _onQuantityChange,
    _findChildLine: _findChildLine,
    // Exposed for the summary-totals lock: "Product Costs" excludes the separately
    // billed logo/branding lines; the Order Total keeps the full ex-VAT subtotal.
    _applyCartTotals: _applyCartTotals
  };
})(window.JRS);
